/**
 * The shared alert list, ported from `feat/alerts-list-querying`.
 *
 * A table rather than a stack of cards: reviewing alerts means comparing them —
 * which machine, how long waiting, what status — and columns let an operator
 * scan one attribute down the list instead of re-reading every row. Queue and
 * History use the SAME projection (the contract returns the same fields), with
 * one column switching: the Queue asks how long an alert has waited, History
 * asks when it was closed.
 *
 * Row focus is registered upwards so the container can drive arrow-key
 * navigation and restore focus when the detail pane closes.
 */
import { useTranslation } from "react-i18next";
import { RotateCcw } from "lucide-react";
import { Badge } from "@/ui/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/ui/components/ui/table";
import { cn } from "@/ui/lib/utils";
import { alertStatusLabel, alertTypeLabel, AlertStatus } from "@/alerts/types/machine-alert";
import { formatSiteTime, waitedFor } from "@/alerts/utils/format";
import type { AlertQueueRow, AlertRow } from "@/alerts/types/alerts-workspace";

/**
 * The canonical status vocabulary verbatim (Pending / Resolved / Ignored) — the
 * list must not re-label what the API and the rest of the app call it.
 */
const STATUS_STYLE: Record<string, string> = {
  [AlertStatus.Pending]: "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300",
  [AlertStatus.Resolved]:
    "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
  [AlertStatus.Ignored]: "border-zinc-500/30 bg-zinc-500/10 text-zinc-600 dark:text-zinc-300",
};

const isQueueRow = (row: AlertRow): row is AlertQueueRow =>
  (row as AlertQueueRow).waiting_since !== undefined;

interface AlertsTableProps {
  items: AlertRow[];
  mode: "queue" | "history";
  selectedId: number | null;
  onSelect: (id: number) => void;
  /** The queue's server clock — row ages must not depend on the browser's. */
  asOf?: string;
  /** Lets the container focus a row for keyboard nav and focus restoration. */
  registerRow?: (id: number, el: HTMLTableRowElement | null) => void;
}

export const AlertsTable = ({
  items,
  mode,
  selectedId,
  onSelect,
  asOf,
  registerRow,
}: AlertsTableProps) => {
  const { t } = useTranslation("alerts");
  return (
  <Table>
    <TableHeader className="sticky top-0 z-[1] bg-background">
      <TableRow className="hover:bg-transparent">
        <TableHead className="w-[72px]">{t("table.columns.evidence")}</TableHead>
        <TableHead>{t("table.columns.alert")}</TableHead>
        <TableHead>{t("table.columns.machine")}</TableHead>
        <TableHead className="hidden xl:table-cell">{t("table.columns.detected")}</TableHead>
        <TableHead>{mode === "queue" ? t("table.columns.waiting") : t("table.columns.closed")}</TableHead>
        {/* History only: the queue is by definition unreviewed. */}
        {mode === "history" && (
          <TableHead className="hidden lg:table-cell">{t("table.columns.reviewedBy")}</TableHead>
        )}
        <TableHead className="w-[104px]">{t("table.columns.status")}</TableHead>
      </TableRow>
    </TableHeader>
    <TableBody>
      {items.map((alert) => {
        const queueRow = isQueueRow(alert) ? alert : null;
        // The workflow only records the first two once it has them.
        const waitingFrom =
          queueRow?.waiting_since ?? alert.platform_received_at ?? alert.timestamp;
        const selected = alert.id === selectedId;

        return (
          <TableRow
            key={alert.id}
            ref={registerRow ? (el) => registerRow(alert.id, el) : undefined}
            tabIndex={0}
            aria-selected={selected}
            data-state={selected ? "selected" : undefined}
            className="h-[58px] cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
            onClick={() => onSelect(alert.id)}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                onSelect(alert.id);
              }
            }}
          >
            {/* No frames, no thumbnail — an alert without evidence should not
                advertise the fact with a crossed-out-image icon in every row.
                The cell itself stays so the column keeps its width and the rows
                stay aligned. */}
            <TableCell>
              {alert.preview?.signed_url && (
                <div className="flex h-9 w-14 items-center justify-center overflow-hidden rounded-md border bg-muted">
                  <img src={alert.preview.signed_url} alt="" className="h-full w-full object-cover" />
                </div>
              )}
            </TableCell>

            <TableCell>
              <div className="min-w-[150px]">
                <div className="flex items-center gap-1.5">
                  <span className="truncate text-sm font-medium">{alertTypeLabel(alert.type)}</span>
                  {queueRow?.reopened && (
                    <RotateCcw
                      className="h-3.5 w-3.5 shrink-0 text-amber-600 dark:text-amber-400"
                      aria-label={t("table.reopened")}
                    />
                  )}
                </div>
                <span className="font-mono text-[11px] text-muted-foreground">
                  #{alert.id}
                  {alert.category ? ` / ${alert.category}` : ""}
                </span>
              </div>
            </TableCell>

            <TableCell>
              <div className="min-w-[110px]">
                <div className="truncate text-xs font-medium">
                  {alert.machine_name || alert.device_mxid}
                </div>
                <div className="truncate font-mono text-[10px] text-muted-foreground">
                  {alert.device_mxid}
                </div>
              </div>
            </TableCell>

            {/* Always the ALERT's own site, never the viewer's zone — see formatSiteTime. */}
            <TableCell className="hidden whitespace-nowrap text-xs text-muted-foreground xl:table-cell">
              {formatSiteTime(alert.timestamp, alert.location_timezone)}
            </TableCell>

            <TableCell className="whitespace-nowrap">
              {mode === "queue" ? (
                <div>
                  <span
                    className="font-mono text-xs font-semibold tabular-nums"
                    title={
                      queueRow?.waiting_since_estimated
                        ? t("table.approximateFrom", { basis: queueRow.waiting_since_basis })
                        : undefined
                    }
                  >
                    {queueRow?.waiting_since_estimated ? "~" : ""}
                    {waitedFor(waitingFrom, asOf)}
                  </span>
                  {/* An approximate age must never read as exact. */}
                  {queueRow?.waiting_since_estimated && (
                    <div className="text-[10px] text-muted-foreground">{t("table.estimated")}</div>
                  )}
                </div>
              ) : (
                <span className="text-xs text-muted-foreground">
                  {formatSiteTime(alert.closed_at ?? alert.timestamp, alert.location_timezone)}
                </span>
              )}
            </TableCell>

            {mode === "history" && (
              <TableCell className="hidden lg:table-cell">
                {/* The row already carries the reviewer as a readable label —
                    `reviewed_by` on the preview belongs to the ML label, not to
                    the alert decision, and resolving ids would fetch a user list
                    for something the payload already answers. */}
                <span className="truncate text-xs text-muted-foreground">
                  {alert.closed_by_label ?? "—"}
                </span>
              </TableCell>
            )}

            <TableCell>
              <Badge variant="outline" className={cn("whitespace-nowrap", STATUS_STYLE[alert.status])}>
                {alertStatusLabel(alert.status)}
              </Badge>
            </TableCell>
          </TableRow>
        );
      })}
    </TableBody>
  </Table>
  );
};
